import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import * as LucideIcons from "lucide-react";
import {
  Briefcase, MapPin, Clock, CheckCircle2, Loader2, AlertCircle, Globe,
  Upload, User, FileText, Link2, ChevronLeft, ChevronRight, Send, Sparkles,
} from "lucide-react";
import {
  apiGetPublicRecruitmentPosition,
  apiSubmitRecruitmentApplication,
  type RecruitmentPublicPosition,
  type RecruitmentCustomField,
} from "@/lib/api";

function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (LucideIcons as any)[name] as React.ElementType | undefined;
  if (!Icon) return <Briefcase className={className} />;
  return <Icon className={className} />;
}

const SUPPORTED_LANGS = [
  { code: "en", label: "EN" },
  { code: "mk", label: "МК" },
];

// ── Country codes ─────────────────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: "+389", iso: "MK", name: "Macedonia" },
  { code: "+355", iso: "AL", name: "Albania" },
  { code: "+43",  iso: "AT", name: "Austria" },
  { code: "+32",  iso: "BE", name: "Belgium" },
  { code: "+387", iso: "BA", name: "Bosnia" },
  { code: "+359", iso: "BG", name: "Bulgaria" },
  { code: "+1",   iso: "CA", name: "Canada" },
  { code: "+385", iso: "HR", name: "Croatia" },
  { code: "+420", iso: "CZ", name: "Czech Rep." },
  { code: "+45",  iso: "DK", name: "Denmark" },
  { code: "+358", iso: "FI", name: "Finland" },
  { code: "+33",  iso: "FR", name: "France" },
  { code: "+49",  iso: "DE", name: "Germany" },
  { code: "+30",  iso: "GR", name: "Greece" },
  { code: "+36",  iso: "HU", name: "Hungary" },
  { code: "+353", iso: "IE", name: "Ireland" },
  { code: "+39",  iso: "IT", name: "Italy" },
  { code: "+383", iso: "XK", name: "Kosovo" },
  { code: "+371", iso: "LV", name: "Latvia" },
  { code: "+370", iso: "LT", name: "Lithuania" },
  { code: "+382", iso: "ME", name: "Montenegro" },
  { code: "+31",  iso: "NL", name: "Netherlands" },
  { code: "+47",  iso: "NO", name: "Norway" },
  { code: "+48",  iso: "PL", name: "Poland" },
  { code: "+351", iso: "PT", name: "Portugal" },
  { code: "+40",  iso: "RO", name: "Romania" },
  { code: "+381", iso: "RS", name: "Serbia" },
  { code: "+421", iso: "SK", name: "Slovakia" },
  { code: "+386", iso: "SI", name: "Slovenia" },
  { code: "+34",  iso: "ES", name: "Spain" },
  { code: "+46",  iso: "SE", name: "Sweden" },
  { code: "+41",  iso: "CH", name: "Switzerland" },
  { code: "+90",  iso: "TR", name: "Turkey" },
  { code: "+44",  iso: "GB", name: "UK" },
  { code: "+1",   iso: "US", name: "USA" },
  { code: "+61",  iso: "AU", name: "Australia" },
];

// ── Validation ────────────────────────────────────────────────────────────────

// Capitalize first letter of every word; strip non-letter chars as user types
function formatName(raw: string): string {
  return raw
    .replace(/[^\p{L}\s\-']/gu, "")
    .replace(/(^|[\s-])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
}

function validateName(value: string): string | null {
  if (!value.trim()) return null;
  return /[^\p{L}\s\-']/u.test(value.trim()) ? "letters_only" : null;
}

function validateEmail(value: string): string | null {
  if (!value.trim()) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? null : "invalid_email";
}

function validateLinkedIn(value: string): string | null {
  if (!value.trim()) return null;
  return /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-_.%]+\/?$/.test(value.trim())
    ? null
    : "invalid_linkedin";
}

function validatePhone(countryCode: string, number: string): string | null {
  if (!number.trim()) return null;
  const digits = number.replace(/[\s\-().]/g, "");
  if (countryCode === "+389") {
    // Strip optional leading 0; then match MK patterns
    const n = digits.startsWith("0") ? digits.slice(1) : digits;
    // Mobile: 7[0-9] + 6 digits = 8 total; Landline Skopje: 2 + 7 digits = 8; Other: (3[1-4]|4[2-8]) + 6 digits = 8
    if (!/^(7[0-9]\d{6}|2\d{7}|(3[1-4]|4[2-8])\d{6})$/.test(n)) return "invalid_phone";
  } else {
    if (!/^\d{6,15}$/.test(digits)) return "invalid_phone";
  }
  return null;
}

// ── Light-mode CSS variable override (prevents dark class bleed-through) ──────
const LIGHT_THEME: React.CSSProperties = {
  colorScheme: "light",
  ["--background" as string]: "248 28% 97%",
  ["--foreground" as string]: "240 10% 10%",
  ["--card" as string]: "0 0% 100%",
  ["--card-foreground" as string]: "240 10% 10%",
  ["--popover" as string]: "0 0% 100%",
  ["--popover-foreground" as string]: "240 10% 10%",
  ["--primary" as string]: "263 70% 58%",
  ["--primary-foreground" as string]: "0 0% 100%",
  ["--secondary" as string]: "263 30% 95%",
  ["--secondary-foreground" as string]: "263 70% 40%",
  ["--muted" as string]: "240 5% 96%",
  ["--muted-foreground" as string]: "240 5% 46%",
  ["--accent" as string]: "263 20% 93%",
  ["--accent-foreground" as string]: "263 70% 40%",
  ["--destructive" as string]: "0 84% 60%",
  ["--destructive-foreground" as string]: "0 0% 100%",
  ["--border" as string]: "240 6% 90%",
  ["--input" as string]: "240 6% 90%",
  ["--ring" as string]: "263 70% 58%",
  ["--radius" as string]: "0.75rem",
};

// ── Shared style constants ────────────────────────────────────────────────────
const inputCls = "h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 rounded-xl focus:border-primary focus:ring-primary";
const textareaCls = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 rounded-xl focus:border-primary resize-none";

// ── Reusable field wrapper ────────────────────────────────────────────────────
function Field({ id, label, required, error, children }: {
  id?: string; label: string; required?: boolean; error?: string; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-gray-700 font-medium text-sm">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Phone input component ─────────────────────────────────────────────────────
function PhoneInput({
  countryCode, onCountryCodeChange, number, onNumberChange, error, label, required,
}: {
  countryCode: string; onCountryCodeChange: (v: string) => void;
  number: string; onNumberChange: (v: string) => void;
  error?: string; label: string; required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-gray-700 font-medium text-sm">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="flex gap-2">
        <select
          value={countryCode}
          onChange={(e) => onCountryCodeChange(e.target.value)}
          className="h-11 rounded-xl border border-gray-300 bg-white text-gray-900 text-sm px-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary shrink-0"
          style={{ minWidth: 110 }}
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.iso + c.code} value={c.code}>
              {c.iso} {c.code}
            </option>
          ))}
        </select>
        <Input
          type="tel"
          value={number}
          onChange={(e) => onNumberChange(e.target.value.replace(/[^\d\s\-().]/g, ""))}
          placeholder={countryCode === "+389" ? "070 123 456" : "123 456 789"}
          className={`flex-1 ${inputCls}`}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 pb-1">
      <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <h3 className="font-semibold text-sm text-gray-900">{children}</h3>
    </div>
  );
}

function FormCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4 ${className}`}>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Apply() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const [position, setPosition] = useState<RecruitmentPublicPosition | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+389");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aiConsentAccepted, setAiConsentAccepted] = useState(false);
  const [customValues, setCustomValues] = useState<Record<number, string | string[] | File>>({});

  // Inline field errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Mobile wizard step
  const [mobileStep, setMobileStep] = useState(1);
  const TOTAL_STEPS = 6;

  // All mandatory conditions met — controls desktop submit button visibility
  const canSubmit = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!email.trim() || validateEmail(email) !== null) return false;
    if (!resumeFile) return false;
    if (!aiConsentAccepted || !termsAccepted) return false;
    if (Object.values(fieldErrors).some((e) => e)) return false;
    if (position) {
      for (const field of position.customFields) {
        if (!field.isRequired) continue;
        const val = customValues[field.id];
        if (field.fieldType === "file" && !(val instanceof File)) return false;
        if (field.fieldType === "multiselect" && (!Array.isArray(val) || val.length === 0)) return false;
        if (field.fieldType !== "file" && field.fieldType !== "multiselect" && (!val || String(val).trim() === "")) return false;
      }
    }
    return true;
  }, [firstName, lastName, email, resumeFile, aiConsentAccepted, termsAccepted, fieldErrors, position, customValues]);

  useEffect(() => { i18n.changeLanguage("mk"); }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    apiGetPublicRecruitmentPosition(slug)
      .then((pos) => { setPosition(pos); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  function setCustomValue(fieldId: number, value: string | string[] | File) {
    setCustomValues((prev) => ({ ...prev, [fieldId]: value }));
  }

  // ── Field-level setters with validation ─────────────────────────────────────
  function setFirstNameValidated(raw: string) {
    const formatted = formatName(raw);
    setFirstName(formatted);
    const err = validateName(formatted);
    setFieldErrors((prev) => ({ ...prev, firstName: err ? t(`recruitment.apply.validFirstName`) : "" }));
  }

  function setLastNameValidated(raw: string) {
    const formatted = formatName(raw);
    setLastName(formatted);
    const err = validateName(formatted);
    setFieldErrors((prev) => ({ ...prev, lastName: err ? t(`recruitment.apply.validLastName`) : "" }));
  }

  function setEmailValidated(val: string) {
    setEmail(val);
    const err = validateEmail(val);
    setFieldErrors((prev) => ({ ...prev, email: err ? t("recruitment.apply.validEmail") : "" }));
  }

  function setLinkedInValidated(val: string) {
    setLinkedinUrl(val);
    if (!val.trim()) { setFieldErrors((prev) => ({ ...prev, linkedin: "" })); return; }
    const err = validateLinkedIn(val);
    setFieldErrors((prev) => ({ ...prev, linkedin: err ? t("recruitment.apply.validLinkedIn") : "" }));
  }

  function setPhoneValidated(code: string, num: string) {
    setPhoneCountryCode(code);
    setPhoneNumber(num);
    if (!num.trim()) { setFieldErrors((prev) => ({ ...prev, phone: "" })); return; }
    const err = validatePhone(code, num);
    setFieldErrors((prev) => ({ ...prev, phone: err ? t("recruitment.apply.validPhone") : "" }));
  }

  // ── Full validation before submit ────────────────────────────────────────────
  function runFullValidation(): boolean {
    const errs: Record<string, string> = {};
    if (validateName(firstName)) errs.firstName = t("recruitment.apply.validFirstName");
    if (validateName(lastName)) errs.lastName = t("recruitment.apply.validLastName");
    if (validateEmail(email)) errs.email = t("recruitment.apply.validEmail");
    if (!resumeFile) errs.resume = t("recruitment.apply.resumeRequired");
    if (linkedinUrl.trim() && validateLinkedIn(linkedinUrl)) errs.linkedin = t("recruitment.apply.validLinkedIn");
    if (phoneNumber.trim() && validatePhone(phoneCountryCode, phoneNumber)) errs.phone = t("recruitment.apply.validPhone");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ── Mobile step validation ───────────────────────────────────────────────────
  function validateStep(step: number): string | null {
    if (step === 2) {
      if (!firstName.trim() || !lastName.trim() || !email.trim()) return t("recruitment.apply.errorRequired");
      if (!runFullValidation()) return " ";
    }
    if (step === 3 && position) {
      if (!resumeFile) return t("recruitment.apply.resumeRequired");
      if (linkedinUrl.trim() && validateLinkedIn(linkedinUrl)) return t("recruitment.apply.validLinkedIn");
      if (phoneNumber.trim() && validatePhone(phoneCountryCode, phoneNumber)) return t("recruitment.apply.validPhone");
      for (const field of position.customFields) {
        if (!field.isRequired) continue;
        const val = customValues[field.id];
        if (field.fieldType === "file" && !(val instanceof File))
          return t("recruitment.apply.errorFieldRequired", { field: field.fieldLabel });
        if (field.fieldType === "multiselect" && (!Array.isArray(val) || val.length === 0))
          return t("recruitment.apply.errorFieldRequired", { field: field.fieldLabel });
        if (field.fieldType !== "file" && field.fieldType !== "multiselect" && (!val || String(val).trim() === ""))
          return t("recruitment.apply.errorFieldRequired", { field: field.fieldLabel });
      }
    }
    if (step === 4 && !aiConsentAccepted) return t("recruitment.apply.aiConsentRequired");
    if (step === 5 && !termsAccepted) return t("recruitment.apply.termsRequired");
    return null;
  }

  function handleMobileNext() {
    const err = validateStep(mobileStep);
    if (err && err !== " ") { setError(err); return; }
    if (err === " ") { setError(t("recruitment.apply.errorRequired")); return; }
    setError(null);
    setMobileStep((s) => Math.min(s + 1, TOTAL_STEPS));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleMobileBack() {
    setError(null);
    setMobileStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // ── Submit ───────────────────────────────────────────────────────────────────
  async function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!position || !slug) return;
    setError(null);

    // Collect ALL field errors at once
    const errs: Record<string, string> = {};
    if (!firstName.trim() || validateName(firstName)) errs.firstName = t("recruitment.apply.validFirstName");
    if (!lastName.trim() || validateName(lastName)) errs.lastName = t("recruitment.apply.validLastName");
    if (!email.trim() || validateEmail(email)) errs.email = t("recruitment.apply.validEmail");
    if (!resumeFile) errs.resume = t("recruitment.apply.resumeRequired");
    if (linkedinUrl.trim() && validateLinkedIn(linkedinUrl)) errs.linkedin = t("recruitment.apply.validLinkedIn");
    if (phoneNumber.trim() && validatePhone(phoneCountryCode, phoneNumber)) errs.phone = t("recruitment.apply.validPhone");
    setFieldErrors(errs);

    // Collect human-readable list of what's missing
    const missingFields: string[] = [];
    if (errs.firstName) missingFields.push(t("recruitment.apply.firstName"));
    if (errs.lastName) missingFields.push(t("recruitment.apply.lastName"));
    if (errs.email) missingFields.push(t("recruitment.apply.email"));
    if (errs.resume) missingFields.push("CV");
    for (const field of position.customFields) {
      if (!field.isRequired) continue;
      const val = customValues[field.id];
      const missing =
        (field.fieldType === "file" && !(val instanceof File)) ||
        (field.fieldType === "multiselect" && (!Array.isArray(val) || val.length === 0)) ||
        (field.fieldType !== "file" && field.fieldType !== "multiselect" && (!val || String(val).trim() === ""));
      if (missing) missingFields.push(field.fieldLabel);
    }
    const missingConsents: string[] = [];
    if (!aiConsentAccepted) missingConsents.push(t("recruitment.apply.aiConsentShort"));
    if (!termsAccepted) missingConsents.push(t("recruitment.apply.termsShort"));

    if (missingFields.length > 0 || missingConsents.length > 0) {
      const parts: string[] = [];
      if (missingFields.length > 0) parts.push(t("recruitment.apply.missingFill", { fields: missingFields.join(", ") }));
      if (missingConsents.length > 0) parts.push(t("recruitment.apply.missingAccept", { fields: missingConsents.join(", ") }));
      setError(parts.join(" · "));
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("firstName", firstName.trim());
      fd.append("lastName", lastName.trim());
      fd.append("email", email.trim());
      const fullPhone = phoneNumber.trim() ? `${phoneCountryCode} ${phoneNumber.trim()}` : "";
      if (fullPhone) fd.append("phone", fullPhone);
      if (coverLetter.trim()) fd.append("coverLetter", coverLetter.trim());
      if (linkedinUrl.trim()) fd.append("linkedinUrl", linkedinUrl.trim());
      if (portfolioUrl.trim()) fd.append("portfolioUrl", portfolioUrl.trim());
      if (resumeFile) fd.append("resume", resumeFile);

      const textAnswers: Record<string, string> = {};
      for (const field of position.customFields) {
        const val = customValues[field.id];
        if (val === undefined) continue;
        if (field.fieldType === "file" && val instanceof File) fd.append(`customFile_${field.id}`, val);
        else if (field.fieldType === "multiselect" && Array.isArray(val)) textAnswers[String(field.id)] = val.join(",");
        else if (typeof val === "string") textAnswers[String(field.id)] = val;
      }
      fd.append("customAnswersJson", JSON.stringify(textAnswers));

      await apiSubmitRecruitmentApplication(slug, fd);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || t("recruitment.apply.errorSubmit"));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f8]" style={LIGHT_THEME}>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (notFound || !position) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f8]" style={LIGHT_THEME}>
        <div className="text-center space-y-3 max-w-sm px-4">
          <AlertCircle className="h-12 w-12 mx-auto text-gray-400" />
          <h1 className="text-xl font-semibold text-gray-900">{t("recruitment.apply.notFound")}</h1>
          <p className="text-gray-500 text-sm">{t("recruitment.apply.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f4f5f8]" style={LIGHT_THEME}>
        <ApplyHeader position={position} />
        <div className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm px-10 py-12 text-center space-y-4 max-w-md w-full">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t("recruitment.apply.successTitle")}</h1>
            <p className="text-gray-500 text-sm leading-relaxed">{t("recruitment.apply.successMessage")}</p>
            <p className="text-xs text-gray-400">{t("recruitment.apply.successClose")}</p>
          </div>
        </div>
        <ApplyFooter />
      </div>
    );
  }

  const JOB_TYPE_LABEL = t(`recruitment.positions.jobTypes.${position.jobType}`, { defaultValue: position.jobType });

  // ── Shared blocks ────────────────────────────────────────────────────────────

  const personalInfoFields = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="firstName" label={t("recruitment.apply.firstName")} required error={fieldErrors.firstName}>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstNameValidated(e.target.value)}
            className={inputCls}
            autoComplete="given-name"
          />
        </Field>
        <Field id="lastName" label={t("recruitment.apply.lastName")} required error={fieldErrors.lastName}>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastNameValidated(e.target.value)}
            className={inputCls}
            autoComplete="family-name"
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="email" label={t("recruitment.apply.email")} required error={fieldErrors.email}>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmailValidated(e.target.value)}
            className={inputCls}
            autoComplete="email"
          />
        </Field>
        <PhoneInput
          label={t("recruitment.apply.phone")}
          countryCode={phoneCountryCode}
          onCountryCodeChange={(code) => setPhoneValidated(code, phoneNumber)}
          number={phoneNumber}
          onNumberChange={(num) => setPhoneValidated(phoneCountryCode, num)}
          error={fieldErrors.phone}
        />
      </div>
    </>
  );

  const materialsFields = (
    <>
      <Field label={t("recruitment.apply.resume").split("(")[0].trim()} required error={fieldErrors.resume}>
        <label
          htmlFor="resume"
          className={`flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
            resumeFile ? "border-primary/40 bg-primary/5" : fieldErrors.resume ? "border-red-400 bg-red-50 hover:border-red-400" : "border-gray-300 bg-white hover:border-primary/40 hover:bg-gray-50"
          }`}
        >
          {resumeFile ? (
            <>
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center break-all">{resumeFile.name}</span>
              <span className="text-xs text-gray-500">{t("recruitment.apply.uploadCvHint")}</span>
            </>
          ) : (
            <>
              <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                <Upload className="h-4 w-4 text-gray-500" />
              </div>
              <span className="text-sm font-medium text-gray-700">{t("recruitment.apply.uploadCvPrompt")}</span>
              <span className="text-xs text-gray-400">{t("recruitment.apply.uploadCvHint")}</span>
            </>
          )}
        </label>
        <input id="resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => { const f = e.target.files?.[0] ?? null; setResumeFile(f); if (f) setFieldErrors((prev) => ({ ...prev, resume: "" })); }} className="sr-only" />
      </Field>

      <Field id="coverLetter" label={t("recruitment.apply.coverLetter")}>
        <Textarea id="coverLetter" value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} rows={5} placeholder={t("recruitment.apply.coverLetterPlaceholder")} className={textareaCls} />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="linkedinUrl" label={t("recruitment.apply.linkedin")} error={fieldErrors.linkedin}>
          <Input
            id="linkedinUrl"
            type="url"
            value={linkedinUrl}
            onChange={(e) => setLinkedInValidated(e.target.value)}
            placeholder="https://linkedin.com/in/yourname"
            className={inputCls}
          />
        </Field>
        <Field id="portfolioUrl" label={t("recruitment.apply.portfolio")}>
          <Input id="portfolioUrl" type="url" value={portfolioUrl} onChange={(e) => setPortfolioUrl(e.target.value)} placeholder={t("recruitment.apply.portfolioPlaceholder")} className={inputCls} />
        </Field>
      </div>

      {position.customFields.length > 0 && (
        <div className="space-y-4 pt-2">
          <p className="text-sm font-semibold text-gray-700">{t("recruitment.apply.additionalInfo")}</p>
          {position.customFields.map((field) => (
            <CustomFieldInput key={field.id} field={field} value={customValues[field.id]} onChange={(val) => setCustomValue(field.id, val)} selectOptionLabel={t("recruitment.apply.selectOption")} />
          ))}
        </div>
      )}
    </>
  );

  // Sara AI consent block
  const aiConsentBlock = (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-violet-50/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <img src="/besthr.png" alt="Sara" className="h-7 w-7 object-contain" />
        </div>
        <div>
          <h3 className="font-bold text-violet-900 flex items-center gap-1.5 text-base">
            {t("recruitment.apply.aiConsentTitle")}
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
          </h3>
          <p className="text-sm text-violet-700 mt-1.5 leading-relaxed">
            {t("recruitment.apply.aiConsentDescription")}
          </p>
        </div>
      </div>
      <div className="flex items-start gap-3 pt-2 border-t border-violet-200/60">
        <Checkbox
          id="aiConsent"
          checked={aiConsentAccepted}
          onCheckedChange={(v) => setAiConsentAccepted(Boolean(v))}
          className="mt-0.5 shrink-0 border-violet-300 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
        />
        <label htmlFor="aiConsent" className="text-sm text-violet-800 cursor-pointer leading-relaxed">
          {t("recruitment.apply.aiConsentLabel")}
        </label>
      </div>
    </div>
  );

  const termsBlock = (
    <FormCard>
      {position.termsAndConditions && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("recruitment.apply.termsLink")}</p>
          <div className="text-xs text-gray-600 whitespace-pre-line border rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">{position.termsAndConditions}</div>
        </div>
      )}
      {position.privacyPolicy && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("recruitment.apply.privacyLink")}</p>
          <div className="text-xs text-gray-600 whitespace-pre-line border rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">{position.privacyPolicy}</div>
        </div>
      )}
      <div className="flex items-start gap-3">
        <Checkbox id="terms" checked={termsAccepted} onCheckedChange={(v) => setTermsAccepted(Boolean(v))} className="mt-0.5 shrink-0" />
        <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
          {t("recruitment.apply.termsLabel")}{" "}
          {position.termsAndConditions ? <span className="font-medium text-gray-900">{t("recruitment.apply.termsLink")}</span> : <button type="button" className="text-primary hover:underline font-medium" onClick={() => window.open("https://besthrapp.com/terms", "_blank")}>{t("recruitment.apply.termsLink")}</button>}
          {" "}{t("recruitment.apply.termsAnd")}{" "}
          {position.privacyPolicy ? <span className="font-medium text-gray-900">{t("recruitment.apply.privacyLink")}</span> : <button type="button" className="text-primary hover:underline font-medium" onClick={() => window.open("https://besthrapp.com/privacy", "_blank")}>{t("recruitment.apply.privacyLink")}</button>}
          .
        </label>
      </div>
    </FormCard>
  );

  const STEP_LABELS = [
    t("recruitment.apply.aboutRole"),
    t("recruitment.apply.personalInfo"),
    t("recruitment.apply.applicationMaterials"),
    t("recruitment.apply.aiConsentTitle"),
    t("recruitment.apply.termsLink"),
    t("recruitment.apply.stepReview"),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f4f5f8]" style={LIGHT_THEME}>
      <ApplyHeader position={position} />

      {/* ── MOBILE wizard ─────────────────────────────────────────────────── */}
      <div className="lg:hidden flex-1 flex flex-col pb-20">
        {/* Step indicator */}
        <div className="bg-white border-b border-gray-200 px-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-400">
              {t("recruitment.apply.stepOf", { current: mobileStep, total: TOTAL_STEPS })}
            </span>
            <span className="text-xs font-semibold text-primary">{STEP_LABELS[mobileStep - 1]}</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${(mobileStep / TOTAL_STEPS) * 100}%` }} />
          </div>
        </div>

        <div className="flex-1 px-4 py-6 space-y-4">
          {/* Step 1: About the role */}
          {mobileStep === 1 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-violet-500/5 px-5 pt-5 pb-4 border-b border-gray-200">
                <div className="h-11 w-11 rounded-xl bg-white/80 border border-gray-200 flex items-center justify-center shadow-sm mb-3">
                  <DynamicIcon name={position.iconName ?? "Briefcase"} className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 leading-tight">{position.title}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                  {position.department && <span className="text-sm font-medium text-gray-800">{position.department}</span>}
                  {position.location && (
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />{position.location}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    <Clock className="h-3 w-3" />{JOB_TYPE_LABEL}
                  </span>
                </div>
              </div>
              <div className="px-5 py-4 space-y-4">
                {position.description && (
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 mb-2">{t("recruitment.apply.aboutRole")}</h2>
                    <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{position.description}</p>
                  </div>
                )}
                {position.requirements && (
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 mb-2">{t("recruitment.apply.requirementsTitle")}</h2>
                    <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{position.requirements}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Personal information */}
          {mobileStep === 2 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2.5 pb-1">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900">{t("recruitment.apply.personalInfo")}</h3>
              </div>
              <Field id="m-firstName" label={t("recruitment.apply.firstName")} required error={fieldErrors.firstName}>
                <Input id="m-firstName" value={firstName} onChange={(e) => setFirstNameValidated(e.target.value)} className={inputCls} autoComplete="given-name" />
              </Field>
              <Field id="m-lastName" label={t("recruitment.apply.lastName")} required error={fieldErrors.lastName}>
                <Input id="m-lastName" value={lastName} onChange={(e) => setLastNameValidated(e.target.value)} className={inputCls} autoComplete="family-name" />
              </Field>
              <Field id="m-email" label={t("recruitment.apply.email")} required error={fieldErrors.email}>
                <Input id="m-email" type="email" value={email} onChange={(e) => setEmailValidated(e.target.value)} className={inputCls} autoComplete="email" />
              </Field>
              <PhoneInput
                label={t("recruitment.apply.phone")}
                countryCode={phoneCountryCode}
                onCountryCodeChange={(code) => setPhoneValidated(code, phoneNumber)}
                number={phoneNumber}
                onNumberChange={(num) => setPhoneValidated(phoneCountryCode, num)}
                error={fieldErrors.phone}
              />
            </div>
          )}

          {/* Step 3: Application materials */}
          {mobileStep === 3 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2.5 pb-1">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900">{t("recruitment.apply.applicationMaterials")}</h3>
              </div>

              <Field label={t("recruitment.apply.resume").split("(")[0].trim()} required error={fieldErrors.resume}>
                <label htmlFor="m-resume" className={`flex flex-col items-center justify-center gap-2 px-4 py-5 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${resumeFile ? "border-primary/40 bg-primary/5" : fieldErrors.resume ? "border-red-400 bg-red-50" : "border-gray-300 bg-gray-50"}`}>
                  {resumeFile ? (
                    <>
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center"><FileText className="h-4 w-4 text-primary" /></div>
                      <span className="text-sm font-medium text-gray-900 text-center break-all">{resumeFile.name}</span>
                    </>
                  ) : (
                    <>
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center"><Upload className="h-4 w-4 text-gray-500" /></div>
                      <span className="text-sm font-medium text-gray-700">{t("recruitment.apply.uploadCvPrompt")}</span>
                      <span className="text-xs text-gray-400">{t("recruitment.apply.uploadCvHint")}</span>
                    </>
                  )}
                </label>
                <input id="m-resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => { const f = e.target.files?.[0] ?? null; setResumeFile(f); if (f) setFieldErrors((prev) => ({ ...prev, resume: "" })); }} className="sr-only" />
              </Field>

              <Field id="m-coverLetter" label={t("recruitment.apply.coverLetter")}>
                <Textarea id="m-coverLetter" value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} rows={4} placeholder={t("recruitment.apply.coverLetterPlaceholder")} className={textareaCls} />
              </Field>

              <Field id="m-linkedin" label={t("recruitment.apply.linkedin")} error={fieldErrors.linkedin}>
                <Input id="m-linkedin" type="url" value={linkedinUrl} onChange={(e) => setLinkedInValidated(e.target.value)} placeholder="https://linkedin.com/in/yourname" className={inputCls} />
              </Field>

              <Field id="m-portfolio" label={t("recruitment.apply.portfolio")}>
                <Input id="m-portfolio" type="url" value={portfolioUrl} onChange={(e) => setPortfolioUrl(e.target.value)} placeholder={t("recruitment.apply.portfolioPlaceholder")} className={inputCls} />
              </Field>

              {position.customFields.length > 0 && (
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <p className="text-sm font-semibold text-gray-700">{t("recruitment.apply.additionalInfo")}</p>
                  {position.customFields.map((field) => (
                    <CustomFieldInput key={field.id} field={field} value={customValues[field.id]} onChange={(val) => setCustomValue(field.id, val)} selectOptionLabel={t("recruitment.apply.selectOption")} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: AI consent */}
          {mobileStep === 4 && aiConsentBlock}

          {/* Step 5: Terms */}
          {mobileStep === 5 && termsBlock}

          {/* Step 6: Review & Submit */}
          {mobileStep === 6 && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                    <Send className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{t("recruitment.apply.stepReview")}</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">{t("recruitment.apply.readyToApplyDesc")}</p>
                <div className="space-y-2 text-sm">
                  {[
                    [t("recruitment.apply.firstName"), `${firstName} ${lastName}`],
                    [t("recruitment.apply.email"), email],
                    ...(resumeFile ? [["CV", resumeFile.name]] : []),
                    [t("recruitment.apply.aiConsentTitle"), aiConsentAccepted ? "✓" : "✗"],
                    [t("recruitment.apply.termsLink"), termsAccepted ? "✓" : "✗"],
                  ].map(([label, value], i, arr) => (
                    <div key={label} className={`flex justify-between py-1.5 ${i < arr.length - 1 ? "border-b border-gray-100" : ""}`}>
                      <span className="text-gray-400">{label}</span>
                      <span className={`font-medium truncate ml-4 ${value === "✓" ? "text-green-600" : value === "✗" ? "text-red-500" : "text-gray-900"}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-200">
                  <AlertCircle className="h-4 w-4 shrink-0" />{error}
                </div>
              )}
            </div>
          )}

          {error && mobileStep < 6 && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-200">
              <AlertCircle className="h-4 w-4 shrink-0" />{error}
            </div>
          )}
        </div>

        {/* Fixed bottom navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 py-3 flex gap-3 z-30">
          {mobileStep > 1 && (
            <Button type="button" variant="outline" onClick={handleMobileBack} className="flex-1 h-12 rounded-xl border-gray-300 text-gray-700">
              <ChevronLeft className="h-4 w-4 mr-1" />{t("recruitment.apply.back")}
            </Button>
          )}
          {mobileStep < TOTAL_STEPS ? (
            <Button type="button" onClick={handleMobileNext} className="flex-1 h-12 rounded-xl font-semibold">
              {t("recruitment.apply.next")}<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={() => handleSubmit()} disabled={submitting} className="flex-1 h-12 rounded-xl font-semibold">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("recruitment.apply.submitting")}</> : <><Send className="h-4 w-4 mr-2" />{t("recruitment.apply.submit")}</>}
            </Button>
          )}
        </div>
      </div>

      {/* ── DESKTOP two-column ────────────────────────────────────────────── */}
      <div className="hidden lg:block flex-1">
        <div className="max-w-7xl mx-auto w-full px-6 py-12">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-8 xl:gap-12 items-start">

            {/* Left: sticky job info */}
            <div className="lg:sticky lg:top-20 overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-violet-500/5 px-6 pt-6 pb-5 border-b border-gray-200">
                <div className="h-12 w-12 rounded-xl bg-white/80 border border-gray-200 flex items-center justify-center shadow-sm mb-4">
                  <DynamicIcon name={position.iconName ?? "Briefcase"} className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 leading-tight">{position.title}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5">
                  {position.department && <span className="text-sm font-medium text-gray-800">{position.department}</span>}
                  {position.location && (
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />{position.location}
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    <Clock className="h-3 w-3 mr-1" />{JOB_TYPE_LABEL}
                  </span>
                </div>
              </div>
              {(position.description || position.requirements) && (
                <div className="bg-white px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                  {position.description && (
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 mb-2">{t("recruitment.apply.aboutRole")}</h2>
                      <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{position.description}</p>
                    </div>
                  )}
                  {position.requirements && (
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 mb-2">{t("recruitment.apply.requirementsTitle")}</h2>
                      <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{position.requirements}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: form */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("recruitment.apply.yourApplication")}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormCard>
                  <SectionHeading icon={<User className="h-4 w-4 text-primary" />}>{t("recruitment.apply.personalInfo")}</SectionHeading>
                  {personalInfoFields}
                </FormCard>

                <FormCard>
                  <SectionHeading icon={<FileText className="h-4 w-4 text-primary" />}>{t("recruitment.apply.applicationMaterials")}</SectionHeading>
                  {materialsFields}
                </FormCard>

                {aiConsentBlock}
                {termsBlock}

                {error && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl border border-red-200">
                    <AlertCircle className="h-4 w-4 shrink-0" />{error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  aria-disabled={!canSubmit}
                  className={`w-full h-12 text-base font-semibold rounded-xl transition-opacity ${!canSubmit && !submitting ? "opacity-40 cursor-not-allowed" : ""}`}
                  size="lg"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("recruitment.apply.submitting")}</> : t("recruitment.apply.submit")}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ApplyFooter />
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
function ApplyHeader({ position }: { position: RecruitmentPublicPosition }) {
  return (
    <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-5">
          {position.companyLogoUrl ? (
            <img src={position.companyLogoUrl} alt={position.companyName ?? "Company"} className="h-20 w-auto object-contain max-w-[240px]" />
          ) : position.companyName ? (
            <span className="font-bold text-2xl text-gray-900">{position.companyName}</span>
          ) : (
            <div className="flex items-center gap-2">
              <Briefcase className="h-8 w-8 text-primary" />
              <span className="font-bold text-xl text-gray-900">Careers</span>
            </div>
          )}
          <span className="text-gray-300 text-3xl font-thin hidden sm:inline">|</span>
          <div className="hidden sm:flex items-center gap-2.5">
            <img src="/besthr.png" alt="BestHR" className="h-14 w-14 object-contain opacity-90" />
            <span className="text-lg font-bold text-gray-900">BestHR</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Globe className="h-3.5 w-3.5 text-gray-400 mr-0.5" />
          {SUPPORTED_LANGS.map((lang, idx) => (
            <span key={lang.code} className="flex items-center">
              {idx > 0 && <span className="text-gray-300 mx-1 text-xs">|</span>}
              <button
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`text-xs font-medium px-1 py-0.5 rounded transition-colors ${i18n.language.startsWith(lang.code) ? "text-primary font-semibold" : "text-gray-400 hover:text-gray-700"}`}
              >
                {lang.label}
              </button>
            </span>
          ))}
        </div>
      </div>
    </header>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function ApplyFooter() {
  return (
    <footer className="sticky bottom-0 z-10 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-6 py-3 flex items-center justify-center gap-2.5">
      <img src="/besthr.png" alt="BestHR" className="h-8 w-8 object-contain opacity-85" />
      <p className="text-sm text-gray-500">
        Powered by <span className="font-bold text-gray-900">BestHR</span> — the smart HR platform built for modern teams.
      </p>
    </footer>
  );
}

// ── Custom field input ────────────────────────────────────────────────────────
function CustomFieldInput({ field, value, onChange, selectOptionLabel }: {
  field: RecruitmentCustomField;
  value: string | string[] | File | undefined;
  onChange: (val: string | string[] | File) => void;
  selectOptionLabel: string;
}) {
  const label = (
    <Label className="text-gray-700 font-medium text-sm">
      {field.fieldLabel}{field.isRequired && <span className="text-red-500 ml-1">*</span>}
    </Label>
  );

  if (field.fieldType === "textarea") {
    return <div className="space-y-1.5">{label}<Textarea value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} rows={4} required={field.isRequired} className={textareaCls} /></div>;
  }
  if (field.fieldType === "select") {
    return (
      <div className="space-y-1.5">
        {label}
        <Select value={typeof value === "string" ? value : ""} onValueChange={onChange}>
          <SelectTrigger className="h-11 bg-white border-gray-300 text-gray-900 rounded-xl"><SelectValue placeholder={selectOptionLabel} /></SelectTrigger>
          <SelectContent>{field.fieldOptions.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (field.fieldType === "multiselect") {
    return (
      <div className="space-y-1.5">
        {label}
        <div className="space-y-2 rounded-xl border border-gray-300 bg-white p-3">
          {field.fieldOptions.map((opt) => {
            const selected = Array.isArray(value) ? value.includes(opt) : false;
            return (
              <div key={opt} className="flex items-center gap-2">
                <Checkbox id={`field-${field.id}-${opt}`} checked={selected} onCheckedChange={(checked) => { const cur = Array.isArray(value) ? value : []; onChange(checked ? [...cur, opt] : cur.filter((v) => v !== opt)); }} />
                <label htmlFor={`field-${field.id}-${opt}`} className="text-sm text-gray-700 cursor-pointer">{opt}</label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.fieldType === "file") {
    return <div className="space-y-1.5">{label}<Input type="file" onChange={(e) => { if (e.target.files?.[0]) onChange(e.target.files[0]); }} required={field.isRequired} className={`${inputCls} cursor-pointer`} /></div>;
  }
  return (
    <div className="space-y-1.5">
      {label}
      <Input
        type={field.fieldType === "number" ? "number" : field.fieldType === "date" ? "date" : field.fieldType === "url" ? "url" : "text"}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.fieldType === "url" ? "https://..." : undefined}
        required={field.isRequired}
        className={inputCls}
      />
    </div>
  );
}
